import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, ApiError, type SupportTicket } from "../lib/api";
import { formatDate, formatDateTime } from "../lib/dateFormat";

const CATEGORY_LABELS: Record<string, string> = {
  bug: "Error",
  duda: "Duda",
  sugerencia: "Sugerencia",
};

type Tab = "open" | "resolved" | "all";

/** «hace 3 días», «hace 2 meses»; pasado un año, la fecha. */
function relativeDate(value: string | null): string {
  if (!value) return "";
  const then = Date.parse(value);
  if (!Number.isFinite(then)) return "";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "hoy";
  if (days === 1) return "ayer";
  if (days < 30) return `hace ${days} días`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? "hace 1 mes" : `hace ${months} meses`;
  return formatDate(value);
}

/** Superadmin inbox for the support tickets Roger files on behalf of admins. */
export default function SuperadminSupportTicketsPage() {
  const { token } = useAuth();
  // Rediseño 2026-10-09: se trae la bandeja completa una vez y las pestañas
  // filtran aquí, para que «Abiertos N» siempre tenga su número.
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [tab, setTab] = useState<Tab>("open");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.supportTickets.list(token);
      setTickets(response.data.tickets);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red. Intenta otra vez.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function updateTicket(ticket: SupportTicket, nextStatus: "open" | "resolved") {
    if (!token) return;
    setSavingId(ticket.id);
    setError(null);
    setMessage(null);
    try {
      await api.supportTickets.update(token, ticket.id, {
        status: nextStatus,
        resolution_note: notes[ticket.id] ?? null,
      });
      setMessage(`Ticket #${ticket.id} ${nextStatus === "resolved" ? "resuelto" : "reabierto"}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red. Intenta otra vez.");
    } finally {
      setSavingId(null);
    }
  }

  const openCount = tickets.filter((ticket) => ticket.status !== "resolved").length;
  const shown = tickets.filter((ticket) =>
    tab === "all" ? true : tab === "resolved" ? ticket.status === "resolved" : ticket.status !== "resolved",
  );

  return (
    <div className="adm-players sa-tickets">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Superadmin · soporte</span>
          <h1 className="adm-players__title">
            Tickets de soporte <span className="adm-players__count">{openCount} abiertos</span>
          </h1>
          <p className="muted sa-section-sub">Problemas y sugerencias que los organizadores reportan desde Roger.</p>
        </div>
        <div className="adm-players__head-actions">
          <Link className="adm-players__btn adm-players__btn--secondary sa-link-btn" to="/superadmin">
            ← Volver
          </Link>
        </div>
      </header>

      <div className="adm-players__chips" role="tablist" aria-label="Estado de los tickets">
        {(["open", "resolved", "all"] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={tab === option}
            className={`adm-players__filter${tab === option ? " is-active" : ""}`}
            onClick={() => setTab(option)}
          >
            {option === "open" ? (
              <>
                Abiertos <span className="adm-fin__chip-count">{openCount}</span>
              </>
            ) : option === "resolved" ? (
              "Resueltos"
            ) : (
              "Todos"
            )}
          </button>
        ))}
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}
      {message && <p className="form-success">{message}</p>}

      {loading ? (
        <p className="muted">Cargando tickets…</p>
      ) : shown.length === 0 ? (
        <p className="muted">
          No hay tickets {tab === "open" ? "abiertos" : tab === "resolved" ? "resueltos" : ""} por ahora.
        </p>
      ) : (
        <div className="sa-tickets__list">
          {shown.map((ticket) => {
            const resolved = ticket.status === "resolved";
            const isBug = ticket.category === "bug";
            const isSuggestion = ticket.category === "sugerencia";
            const contextPath = ticket.context_page && ticket.context_page.startsWith("/") ? ticket.context_page : null;
            return (
              <article key={ticket.id} className={`sa-ticket${resolved ? " is-resolved" : ""}`}>
                <div className="sa-ticket__chips">
                  <span className={`adm-tourn__chip${isBug ? " sa-chip--bug" : isSuggestion ? " sa-chip--idea" : ""}`}>
                    {CATEGORY_LABELS[ticket.category] ?? ticket.category}
                  </span>
                  <span className={`adm-tourn__chip${resolved ? " sa-chip--done" : " adm-tourn__chip--warn"}`}>
                    {resolved ? "Resuelto" : "Abierto"}
                  </span>
                  <span className="sa-ticket__when" title={formatDateTime(ticket.created_at)}>
                    #{ticket.id} · {relativeDate(ticket.created_at)}
                  </span>
                </div>
                <h2 className="sa-ticket__title">{ticket.summary}</h2>
                {isSuggestion ? (
                  <blockquote className="sa-ticket__quote">{ticket.detail}</blockquote>
                ) : (
                  <p className="sa-ticket__detail">{ticket.detail}</p>
                )}
                <div className="sa-ticket__meta">
                  <span className="adm-tourn__chip" title={ticket.created_by_email ?? undefined}>
                    {ticket.created_by_name ?? ticket.created_by_email ?? "—"}
                  </span>
                  {ticket.tournament_name ? (
                    <span className="adm-tourn__chip">Torneo «{ticket.tournament_name}»</span>
                  ) : null}
                  {contextPath ? (
                    <Link className="sa-ticket__open" to={contextPath} title={contextPath}>
                      Abrir la pantalla ›
                    </Link>
                  ) : ticket.context_page ? (
                    <span className="muted">{ticket.context_page}</span>
                  ) : null}
                </div>
                {ticket.resolution_note && resolved ? (
                  <p className="muted">Resolución: {ticket.resolution_note}</p>
                ) : null}
                <div className="sa-ticket__actions">
                  {!resolved ? (
                    <>
                      <input
                        type="text"
                        placeholder="Nota de resolución (opcional)"
                        aria-label={`Nota de resolución del ticket #${ticket.id}`}
                        value={notes[ticket.id] ?? ""}
                        onChange={(event) => setNotes((current) => ({ ...current, [ticket.id]: event.target.value }))}
                        maxLength={2000}
                      />
                      <button
                        type="button"
                        className="btn btn--compact sa-ticket__resolve"
                        disabled={savingId === ticket.id}
                        onClick={() => void updateTicket(ticket, "resolved")}
                      >
                        {savingId === ticket.id ? "Guardando…" : "Marcar resuelto"}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--compact btn--ghost"
                      disabled={savingId === ticket.id}
                      onClick={() => void updateTicket(ticket, "open")}
                    >
                      {savingId === ticket.id ? "Guardando…" : "Reabrir"}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
